import {useId, useState, type ReactNode} from 'react';
import {AlertTriangle, ArrowDown, Link2, Plus} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {
  TextField,
  TextArea,
  RichTextEligibleField,
  Field,
  EditorCard,
  IconSelect,
  moveItem,
  sameConfig,
  UseDefaultToggle,
  type IconOption,
  type SectionProps,
} from '../controls';
import {PdfUploadField} from '../PdfUploadField';
import {MirrorLock, useMirroredField} from '../mirrorLock';
import {CONTACT_TOKEN, DEFAULT_DISCLOSURES} from '@common/enrollmentSite/defaults/disclosures';
import {DEFAULT_HIGHLIGHT} from '@common/enrollmentSite/defaults/highlight';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@common-web/components/ui/alert-dialog';
import {COVERAGE_ICON_POOL, RESOURCE_ICONS} from '@client/pages/public/enroll/coverageIcons';
import {
  SITE_PAGES,
  absoluteHref,
  isPdfHref,
  sitePageHref,
  sitePageOf,
  type SitePage,
} from '@common/enrollmentSite/siteLinks';
import type {
  CoverageIcon,
  HighlightCard,
  LandingResource,
  NoticesPageContent,
  ResourceDisclosure,
  ResourceDoc,
  ResourcesPageContent,
  ResourcesSection as ResourcesBlock,
} from '@common/enrollmentSite/types';

const RESOURCE_ICON_OPTIONS: IconOption[] = Object.entries(RESOURCE_ICONS).map(
  ([value, {Icon, label}]) => ({value, label, Icon}),
);
const COVERAGE_ICON_OPTIONS: IconOption[] = Object.entries(COVERAGE_ICON_POOL).map(
  ([value, {Icon, label}]) => ({value, label, Icon}),
);

// Empty blocks are stored as absent, so the public site shows its "not available yet" state.
const blockOrUndefined = (b: ResourcesBlock) =>
  !b.title && !b.intro && !b.items.length ? undefined : b;
// A stored highlight/disclosures choice (including "none") counts as content.
const resourcesPageOrUndefined = (p: ResourcesPageContent) =>
  !p.title &&
  !p.intro &&
  !p.documents.length &&
  p.disclosures === undefined &&
  p.virtualCare === undefined
    ? undefined
    : p;
const noticesPageOrUndefined = (p: NoticesPageContent) =>
  !p.title &&
  !p.intro &&
  !p.documents.length &&
  p.disclosures === undefined &&
  p.highlight === undefined
    ? undefined
    : p;

export function ResourcesSection({config, patch, groupSlug, isNew}: SectionProps) {
  const block = config.resourcesSection ?? {intro: '', items: []};
  const resourcesPage = config.resourcesPage ?? {intro: '', documents: []};
  const noticesPage = config.noticesPage ?? {intro: '', documents: []};
  const {isMirrored, sourceName} = useMirroredField();
  const [openCard, setOpenCard] = useState<number | null>(null);
  const [flashed, setFlashed] = useState<string | null>(null);

  const setBlock = (next: ResourcesBlock) => patch({resourcesSection: blockOrUndefined(next)});
  const setResourcesPage = (next: ResourcesPageContent) =>
    patch({resourcesPage: resourcesPageOrUndefined(next)});
  const setNoticesPage = (next: NoticesPageContent) =>
    patch({noticesPage: noticesPageOrUndefined(next)});

  function jumpTo(part: string) {
    document.getElementById(partId(part))?.scrollIntoView({behavior: 'smooth', block: 'start'});
    setFlashed(part);
    setTimeout(() => setFlashed((f) => (f === part ? null : f)), 1500);
  }

  function addCardFor(page: SitePage) {
    setOpenCard(block.items.length);
    setBlock({
      ...block,
      items: [
        ...block.items,
        {
          icon: page,
          title: page === 'resources' ? 'Plan Resources' : 'Annual Notices',
          description: '',
          href: sitePageHref(config.slug, page),
        },
      ],
    });
    jumpTo('cards');
  }

  const pageStatus = (page: SitePage) => (
    <PageLinkStatus
      page={page}
      hasContent={!!(page === 'resources' ? config.resourcesPage : config.noticesPage)}
      cards={block.items}
      slug={config.slug}
      cardsLockedBy={isMirrored('resourcesSection') ? sourceName : undefined}
      onAddCard={() => addCardFor(page)}
    />
  );

  return (
    <div className='space-y-5'>
      <MirrorLock field='resourcesVisibility'>
        <Field
          label='Enable resources section'
          hint='Landing page resource cards, plus the Plan resources and Annual notices pages they link to. Content is kept when off.'
        >
          <label className='flex items-center gap-2 text-sm'>
            {/* Enabled is the default — store disabled explicitly, enabled as omitted. */}
            <input
              type='checkbox'
              checked={!config.resourcesHidden}
              onChange={(e) => patch({resourcesHidden: e.target.checked ? undefined : true})}
            />
            Show resources on the site
          </label>
        </Field>
      </MirrorLock>

      {!config.resourcesHidden && (
        <>
          <PartCard
            part='cards'
            flashed={flashed}
            title='Landing resource cards'
            tag='landing page'
          >
            <MirrorLock field='resourcesSection'>
              <TextField
                label='Section title'
                hint='Leave blank to use “Want to read up first?”.'
                placeholder='Want to read up first?'
                value={block.title}
                onChange={(title) => setBlock({...block, title: title || undefined})}
              />
              <TextArea
                label='Intro'
                rows={2}
                value={block.intro}
                onChange={(intro) => setBlock({...block, intro})}
              />
              <Field
                label='Cards'
                hintTop
                hint='The block is hidden until it has at least one card.'
              >
                <CardsEditor
                  cards={block.items}
                  slug={config.slug}
                  groupSlug={groupSlug}
                  isNew={isNew}
                  openIndex={openCard}
                  onOpenIndex={setOpenCard}
                  onEditPage={jumpTo}
                  onChange={(items) => setBlock({...block, items})}
                />
              </Field>
            </MirrorLock>
          </PartCard>

          <PartCard
            part='resources'
            flashed={flashed}
            title='Plan resources page'
            tag={`/${config.slug}/resources`}
          >
            {pageStatus('resources')}
            <MirrorLock field='resourcesPage'>
              <TextField
                label='Page title'
                hint='Leave blank to use “Plan Resources”.'
                placeholder='Plan Resources'
                value={resourcesPage.title}
                onChange={(title) =>
                  setResourcesPage({...resourcesPage, title: title || undefined})
                }
              />
              <TextArea
                label='Intro'
                rows={2}
                value={resourcesPage.intro}
                onChange={(intro) => setResourcesPage({...resourcesPage, intro})}
              />
            </MirrorLock>
            <MirrorLock field='resourcesPageDocs'>
              <Field label='Documents' hintTop hint='Shown as cards on the page.'>
                <DocumentsEditor
                  docs={resourcesPage.documents}
                  groupSlug={groupSlug}
                  isNew={isNew}
                  slug={config.slug}
                  onChange={(documents) => setResourcesPage({...resourcesPage, documents})}
                />
              </Field>
            </MirrorLock>
            <MirrorLock field='resourcesPage'>
              <HighlightCardEditor
                value={resourcesPage.virtualCare}
                onChange={(virtualCare) => setResourcesPage({...resourcesPage, virtualCare})}
              />
              <DisclosuresEditor
                items={resourcesPage.disclosures}
                onChange={(disclosures) => setResourcesPage({...resourcesPage, disclosures})}
              />
            </MirrorLock>
          </PartCard>

          <PartCard
            part='notices'
            flashed={flashed}
            title='Annual notices page'
            tag={`/${config.slug}/notices`}
          >
            {pageStatus('notices')}
            <MirrorLock field='noticesPage'>
              <TextField
                label='Page title'
                hint='Leave blank to use “Annual Notices”.'
                placeholder='Annual Notices'
                value={noticesPage.title}
                onChange={(title) => setNoticesPage({...noticesPage, title: title || undefined})}
              />
              <TextArea
                label='Intro'
                rows={2}
                value={noticesPage.intro}
                onChange={(intro) => setNoticesPage({...noticesPage, intro})}
              />
            </MirrorLock>
            <MirrorLock field='noticesPageDocs'>
              <Field label='Notices' hintTop hint='Shown as cards on the page.'>
                <DocumentsEditor
                  docs={noticesPage.documents}
                  groupSlug={groupSlug}
                  isNew={isNew}
                  slug={config.slug}
                  addLabel='Add notice'
                  onChange={(documents) => setNoticesPage({...noticesPage, documents})}
                />
              </Field>
            </MirrorLock>
            <MirrorLock field='noticesPage'>
              <HighlightCardEditor
                value={noticesPage.highlight}
                onChange={(highlight) => setNoticesPage({...noticesPage, highlight})}
              />
              <DisclosuresEditor
                items={noticesPage.disclosures}
                onChange={(disclosures) => setNoticesPage({...noticesPage, disclosures})}
              />
            </MirrorLock>
          </PartCard>
        </>
      )}
    </div>
  );
}

const partId = (part: string) => `resources-part-${part}`;

/** Top-level part of the section, styled like the Products step cards. */
function PartCard({
  part,
  flashed,
  title,
  tag,
  children,
}: {
  part: string;
  flashed: string | null;
  title: string;
  tag: string;
  children: ReactNode;
}) {
  return (
    <div
      id={partId(part)}
      className={`scroll-mt-4 overflow-hidden rounded-lg border transition-shadow ${
        flashed === part ? 'ring-2 ring-primary' : ''
      }`}
    >
      <div className='flex items-center gap-2 border-b bg-muted/30 px-3 py-2'>
        <span className='flex-1 truncate text-sm font-semibold'>{title}</span>
        <span className='truncate text-xs text-muted-foreground'>{tag}</span>
      </div>
      <div className='space-y-4 p-3'>{children}</div>
    </div>
  );
}

/** Which landing cards lead to a page; pages without one are only reachable by URL. */
function PageLinkStatus({
  page,
  hasContent,
  cards,
  slug,
  cardsLockedBy,
  onAddCard,
}: {
  page: SitePage;
  /** An empty page has nothing to reach, so a missing card isn't worth flagging. */
  hasContent: boolean;
  cards: LandingResource[];
  slug: string;
  cardsLockedBy?: string;
  onAddCard: () => void;
}) {
  const linking = cards.filter((c) => sitePageOf(c.href, slug) === page);
  if (linking.length) {
    const names = linking.map((c) => `“${c.title || 'Untitled'}”`).join(', ');
    return (
      <p className='flex items-center gap-1.5 text-xs text-muted-foreground'>
        <Link2 className='h-3 w-3 shrink-0' />
        Linked from the {names} landing card{linking.length > 1 ? 's' : ''}.
      </p>
    );
  }
  if (!hasContent) return null;
  return (
    <div className='flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200'>
      <span className='flex flex-1 items-start gap-1.5'>
        <AlertTriangle className='mt-px h-3.5 w-3.5 shrink-0' />
        <span>
          No landing card links here. Members can only reach it by direct URL.
          {cardsLockedBy && (
            <span className='block'>
              Landing cards are mirrored, so add one on {cardsLockedBy}.
            </span>
          )}
        </span>
      </span>
      {!cardsLockedBy && (
        <Button variant='outline' size='sm' className='h-7 bg-background' onClick={onAddCard}>
          <Plus className='mr-1 h-3.5 w-3.5' />
          Add a card
        </Button>
      )}
    </div>
  );
}

function CardsEditor({
  cards,
  slug,
  groupSlug,
  isNew,
  openIndex,
  onOpenIndex,
  onEditPage,
  onChange,
}: {
  cards: LandingResource[];
  slug: string;
  groupSlug: string;
  isNew: boolean;
  /** Existing cards start collapsed; a newly added one opens. */
  openIndex: number | null;
  onOpenIndex: (i: number) => void;
  onEditPage: (page: SitePage) => void;
  onChange: (cards: LandingResource[]) => void;
}) {
  const update = (i: number, next: Partial<LandingResource>) =>
    onChange(cards.map((c, idx) => (idx === i ? {...c, ...next} : c)));
  return (
    <div className='space-y-3'>
      {cards.map((card, i) => (
        <EditorCard
          key={i}
          index={i}
          count={cards.length}
          title={card.title || 'Untitled card'}
          subtitle={linkSummary(card.href, slug)}
          collapsible
          defaultOpen={i === openIndex}
          onMove={(dir) => onChange(moveItem(cards, i, dir))}
          onRemove={() => onChange(cards.filter((_, idx) => idx !== i))}
        >
          <div className='flex items-end gap-3'>
            <Field label='Icon'>
              <IconSelect
                value={card.icon}
                onChange={(icon) => update(i, {icon: icon as LandingResource['icon']})}
                options={RESOURCE_ICON_OPTIONS}
                columns={4}
              />
            </Field>
            <div className='flex-1'>
              <TextField
                label='Title'
                value={card.title}
                onChange={(title) => update(i, {title})}
              />
            </div>
          </div>
          <TextArea
            label='Description'
            rows={2}
            value={card.description}
            onChange={(description) => update(i, {description})}
          />
          <ResourceLinkField
            href={card.href}
            slug={slug}
            allowPages
            groupSlug={groupSlug}
            isNew={isNew}
            onChange={(href, external) => update(i, {href, external: external || undefined})}
          />
          <EditPageLink page={sitePageOf(card.href, slug)} onEditPage={onEditPage} />
        </EditorCard>
      ))}
      <Button
        variant='outline'
        size='sm'
        onClick={() => {
          onOpenIndex(cards.length);
          onChange([
            ...cards,
            {icon: 'resources', title: '', description: '', href: sitePageHref(slug, 'resources')},
          ]);
        }}
      >
        <Plus className='mr-1.5 h-3.5 w-3.5' />
        Add card
      </Button>
    </div>
  );
}

function EditPageLink({
  page,
  onEditPage,
}: {
  page: SitePage | undefined;
  onEditPage: (page: SitePage) => void;
}) {
  const label = SITE_PAGES.find((p) => p.page === page)?.label;
  if (!page || !label) return null;
  // An anchor, not a button, so it still works inside a mirrored (disabled) fieldset.
  return (
    <a
      href={`#${partId(page)}`}
      className='inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline'
      onClick={(e) => {
        e.preventDefault();
        onEditPage(page);
      }}
    >
      Edit the {label}
      <ArrowDown className='h-3 w-3' />
    </a>
  );
}

function DocumentsEditor({
  docs,
  groupSlug,
  isNew,
  slug,
  addLabel = 'Add document',
  onChange,
}: {
  docs: ResourceDoc[];
  groupSlug: string;
  isNew: boolean;
  slug: string;
  addLabel?: string;
  onChange: (docs: ResourceDoc[]) => void;
}) {
  const [addedIndex, setAddedIndex] = useState<number | null>(null);
  const update = (i: number, next: Partial<ResourceDoc>) =>
    onChange(docs.map((d, idx) => (idx === i ? {...d, ...next} : d)));
  return (
    <div className='space-y-3'>
      {docs.map((doc, i) => (
        <EditorCard
          key={i}
          index={i}
          count={docs.length}
          title={doc.title || 'Untitled document'}
          subtitle={linkSummary(doc.href, slug)}
          collapsible
          defaultOpen={i === addedIndex}
          onMove={(dir) => onChange(moveItem(docs, i, dir))}
          onRemove={() => onChange(docs.filter((_, idx) => idx !== i))}
        >
          <TextField label='Title' value={doc.title} onChange={(title) => update(i, {title})} />
          <TextArea
            label='Description'
            rows={2}
            value={doc.description}
            onChange={(description) => update(i, {description})}
          />
          <ResourceLinkField
            href={doc.href}
            slug={slug}
            groupSlug={groupSlug}
            isNew={isNew}
            onChange={(href) => update(i, {href})}
          />
        </EditorCard>
      ))}
      <Button
        variant='outline'
        size='sm'
        onClick={() => {
          setAddedIndex(docs.length);
          onChange([...docs, {title: '', description: '', href: ''}]);
        }}
      >
        <Plus className='mr-1.5 h-3.5 w-3.5' />
        {addLabel}
      </Button>
    </div>
  );
}

type HighlightMode = 'default' | 'custom' | 'none';

const HIGHLIGHT_MODES: {mode: HighlightMode; label: string}[] = [
  {mode: 'default', label: 'Shared default'},
  {mode: 'custom', label: 'Custom'},
  {mode: 'none', label: 'None'},
];

function HighlightCardEditor({
  value,
  onChange,
}: {
  value: HighlightCard | null | undefined;
  onChange: (next: HighlightCard | null | undefined) => void;
}) {
  const name = useId();
  const mode: HighlightMode = value === undefined ? 'default' : value === null ? 'none' : 'custom';
  const [confirmMode, setConfirmMode] = useState<HighlightMode | null>(null);

  const apply = (next: HighlightMode) =>
    onChange(
      next === 'default' ? undefined : next === 'none' ? null : structuredClone(DEFAULT_HIGHLIGHT),
    );
  function pick(next: HighlightMode) {
    if (next === mode) return;
    if (value && !sameConfig(value, DEFAULT_HIGHLIGHT)) setConfirmMode(next);
    else apply(next);
  }

  return (
    <Field
      label='Highlight card'
      hint='Card beside the disclosures, e.g. virtual care or pharmacy.'
    >
      <div className='space-y-3'>
        <div className='flex flex-wrap gap-x-4 gap-y-1.5 text-sm'>
          {HIGHLIGHT_MODES.map((m) => (
            <label key={m.mode} className='flex items-center gap-2'>
              <input
                type='radio'
                name={name}
                checked={mode === m.mode}
                onChange={() => pick(m.mode)}
              />
              {m.label}
            </label>
          ))}
        </div>
        {mode === 'default' && (
          <div className='rounded-lg border bg-muted/20 p-3 text-sm text-muted-foreground'>
            {DEFAULT_HIGHLIGHT.title}
          </div>
        )}
        {value && (
          <div className='space-y-3 rounded-lg border p-3'>
            <div className='flex items-end gap-3'>
              <Field label='Icon'>
                <IconSelect
                  value={value.icon ?? 'stethoscope'}
                  onChange={(icon) =>
                    onChange({
                      ...value,
                      icon: icon === 'stethoscope' ? undefined : (icon as CoverageIcon),
                    })
                  }
                  options={COVERAGE_ICON_OPTIONS}
                />
              </Field>
              <div className='flex-1'>
                <TextField
                  label='Title'
                  value={value.title}
                  onChange={(title) => onChange({...value, title})}
                />
              </div>
            </div>
            <RichTextEligibleField
              label='Body'
              value={value.body}
              onChange={(body) => onChange({...value, body})}
            />
            <RichTextEligibleField
              label='Closing line'
              value={value.closing}
              onChange={(closing) => onChange({...value, closing: closing || undefined})}
            />
          </div>
        )}
      </div>
      <AlertDialog
        open={confirmMode !== null}
        onOpenChange={(open) => !open && setConfirmMode(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this custom card?</AlertDialogTitle>
            <AlertDialogDescription>
              This page’s own highlight card will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmMode) apply(confirmMode);
                setConfirmMode(null);
              }}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Field>
  );
}

function DisclosuresEditor({
  items,
  onChange,
}: {
  items: ResourceDisclosure[] | undefined;
  onChange: (items: ResourceDisclosure[] | undefined) => void;
}) {
  const usingDefault = items === undefined;
  const list = items ?? [];
  const [addedIndex, setAddedIndex] = useState<number | null>(null);
  const update = (i: number, next: Partial<ResourceDisclosure>) =>
    onChange(list.map((d, idx) => (idx === i ? {...d, ...next} : d)));
  return (
    <div className='space-y-3'>
      <UseDefaultToggle
        label='Required disclosures'
        hint={`Listed on the page under "Required disclosures". ${CONTACT_TOKEN} becomes the site’s benefits contact wording.`}
        usingDefault={usingDefault}
        isCustomized={!usingDefault && !sameConfig(items, DEFAULT_DISCLOSURES)}
        onChange={(useDefault) =>
          onChange(useDefault ? undefined : structuredClone(DEFAULT_DISCLOSURES))
        }
      />
      {usingDefault ? (
        <ul className='space-y-1 rounded-lg border bg-muted/20 p-3 text-sm text-muted-foreground'>
          {DEFAULT_DISCLOSURES.map((d) => (
            <li key={d.label}>{d.label}</li>
          ))}
        </ul>
      ) : (
        <div className='space-y-3'>
          {list.map((d, i) => (
            <EditorCard
              key={i}
              index={i}
              count={list.length}
              title={d.label || 'Untitled disclosure'}
              collapsible
              defaultOpen={i === addedIndex}
              onMove={(dir) => onChange(moveItem(list, i, dir))}
              onRemove={() => onChange(list.filter((_, idx) => idx !== i))}
            >
              <TextField label='Label' value={d.label} onChange={(label) => update(i, {label})} />
              <RichTextEligibleField
                label='Text'
                value={d.text}
                onChange={(text) => update(i, {text})}
              />
            </EditorCard>
          ))}
          <Button
            variant='outline'
            size='sm'
            onClick={() => {
              setAddedIndex(list.length);
              onChange([...list, {label: '', text: ''}]);
            }}
          >
            <Plus className='mr-1.5 h-3.5 w-3.5' />
            Add disclosure
          </Button>
        </div>
      )}
    </div>
  );
}

type LinkKind = SitePage | 'pdf' | 'url';

/** Short "where it goes" label for a collapsed card header. */
function linkSummary(href: string, slug: string): string {
  const page = SITE_PAGES.find((p) => p.page === sitePageOf(href, slug));
  if (page) return `→ ${page.label}`;
  if (!href) return 'No link yet';
  if (isPdfHref(href) || href.includes('/uploads/')) return 'PDF';
  try {
    return new URL(href).hostname;
  } catch {
    return 'Web link';
  }
}

function kindOf(href: string, slug: string): LinkKind {
  const page = sitePageOf(href, slug);
  if (page) return page;
  // Uploaded or site-hosted files; any other address (even one ending in .pdf) is a web link.
  if (href.includes('/uploads/') || href.startsWith('/')) return 'pdf';
  return 'url';
}

/** Where a card or document points: one of this site's pages, an uploaded PDF, or a web link. */
function ResourceLinkField({
  href,
  slug,
  allowPages = false,
  groupSlug,
  isNew,
  onChange,
}: {
  href: string;
  slug: string;
  allowPages?: boolean;
  groupSlug: string;
  isNew: boolean;
  onChange: (href: string, external: boolean) => void;
}) {
  const name = useId();
  // Read from the link itself (list items are keyed by position, so state would drift on
  // remove/reorder); only an empty link needs to remember which input to show.
  const [emptyKind, setEmptyKind] = useState<'pdf' | 'url'>('pdf');
  const kind: LinkKind = href ? kindOf(href, slug) : emptyKind;
  const options: {kind: LinkKind; label: string}[] = [
    ...(allowPages ? SITE_PAGES.map((p) => ({kind: p.page as LinkKind, label: p.label})) : []),
    {kind: 'pdf', label: 'Uploaded PDF'},
    {kind: 'url', label: 'Web link'},
  ];

  function pick(next: LinkKind) {
    if (next === kind) return;
    if (next === 'resources' || next === 'notices') {
      onChange(sitePageHref(slug, next), false);
      return;
    }
    setEmptyKind(next);
    onChange('', true);
  }

  return (
    <Field label='Links to'>
      <div className='space-y-2'>
        <div className='flex flex-wrap gap-x-4 gap-y-1.5 text-sm'>
          {options.map((o) => (
            <label key={o.kind} className='flex items-center gap-2'>
              <input
                type='radio'
                name={name}
                checked={kind === o.kind}
                onChange={() => pick(o.kind)}
              />
              {o.label}
            </label>
          ))}
        </div>
        {kind === 'pdf' && (
          <PdfUploadField
            groupSlug={groupSlug}
            isNew={isNew}
            value={href || undefined}
            onChange={(url) => onChange(url ?? '', true)}
          />
        )}
        {kind === 'url' && (
          <Input
            value={href}
            placeholder='https://...'
            onChange={(e) => onChange(e.target.value.trim(), true)}
            onBlur={() => href !== absoluteHref(href) && onChange(absoluteHref(href), true)}
          />
        )}
      </div>
    </Field>
  );
}
